import type { KeyboardEvent, ReactNode } from "react";
import { Button } from "./Button";
import { cx } from "./cx";
import { pluralize } from "./format";
import { Spinner } from "./Spinner";
import { Toolbar } from "./Toolbar";
import { VisuallyHidden } from "./VisuallyHidden";
import "./BulkBar.css";

export type Noun = string | { one: string; other: string };

export interface BulkBarProps {
  /** Selected count; the bar is not rendered at 0. */
  count: number;
  /** "channel" or { one: "channel", other: "channels" }. Default "item". */
  noun?: Noun;
  /** Buttons: first primary, others secondary/ghost, destructive last. Name the count. */
  actions: ReactNode;
  /** "Clear selection" button and Esc. */
  onClear: () => void;
  /** Offer "Select all N matching" when a filter has more rows than are visible. */
  onSelectAll?: () => void;
  /** Total matching rows for the select-all link. */
  totalCount?: number;
  /** Disables actions and shows a spinner beside the count. */
  busy?: boolean;
  /** Stick to the bottom of the scroll container (default true). */
  sticky?: boolean;
  className?: string;
}

/**
 * Appears when ≥1 row is selected: "N selected", bulk actions, Clear.
 * role="toolbar" (aria-label "Bulk actions"); the count is announced politely.
 */
export function BulkBar({
  count,
  noun = "item",
  actions,
  onClear,
  onSelectAll,
  totalCount,
  busy = false,
  sticky = true,
  className,
}: BulkBarProps) {
  const text = `${pluralize(count, noun)} selected`;
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClear();
    }
  };

  return (
    <>
      {/* Always mounted so the first selection is announced too. */}
      <VisuallyHidden aria-live="polite">{count > 0 ? text : ""}</VisuallyHidden>
      {count > 0 ? (
        <div
          className={cx("ui-bulkbar", sticky && "ui-bulkbar--sticky", className)}
          aria-busy={busy || undefined}
        >
          <Toolbar label="Bulk actions" className="ui-bulkbar__toolbar" onKeyDown={onKeyDown}>
            <span className="ui-bulkbar__count">
              {busy ? <Spinner size="sm" label="Working" /> : null}
              <strong>{pluralize(count, noun)}</strong>&nbsp;selected
            </span>
            {onSelectAll && totalCount !== undefined && totalCount > count ? (
              <Button size="sm" variant="ghost" onClick={onSelectAll} disabled={busy}>
                Select all {pluralize(totalCount, noun)}
              </Button>
            ) : null}
            <fieldset className="ui-bulkbar__actions" disabled={busy}>
              {actions}
            </fieldset>
            <Button size="sm" variant="ghost" onClick={onClear} disabled={busy}>
              Clear selection
            </Button>
          </Toolbar>
        </div>
      ) : null}
    </>
  );
}
